import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit3, X, Truck, FileText, Car, Settings, ExternalLink, Sun, Moon, DollarSign, Briefcase, Package, AlertCircle } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../services/api';
import { API } from '../App';
import { toast } from 'sonner';
import DashboardLinkModal from './DashboardLinkModal';
import { useTheme } from '../providers/ThemeProvider';

const DashboardDropdown = ({ isOpen, user, onClose, isMobileMenu = false }) => {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const dropdownRef = useRef(null);
  const { theme, setTheme } = useTheme();
  
  // Check if user has permission to see a link
  const canAccessLink = (link) => {
    // Super admin can see everything
    if (user?.role === 'super_admin') return true;
    
    // If link has a permission_key, use that first (from hub apps)
    if (link.permission_key) {
      return user?.[link.permission_key] || false;
    }
    
    // External links check visible_to_all flag
    if (link.is_external) {
      return link.visible_to_all === true;
    }
    
    // Internal app links - check page permissions based on URL/name (fallback for custom links)
    const linkName = link.name?.toLowerCase() || '';
    const linkUrl = link.url?.toLowerCase() || '';
    
    if (linkUrl.includes('transport') || linkName.includes('transport')) {
      return user?.can_access_transport || false;
    }
    if (linkUrl.includes('report') || linkName.includes('report')) {
      return user?.can_access_reports || false;
    }
    if (linkUrl.includes('company-car') || linkUrl.includes('companycar') || linkName.includes('company car')) {
      return user?.can_access_company_car || false;
    }
    if (linkUrl.includes('setting') || linkName.includes('setting')) {
      return user?.can_access_settings || false;
    }
    if (linkUrl.includes('sales') || linkName.includes('sales')) {
      return user?.can_access_sales || false;
    }
    if (linkUrl.includes('error-log') || linkUrl.includes('error_log') || linkName.includes('error log')) {
      return user?.can_view_error_log || false;
    }
    if (linkUrl.includes('pi-count') || linkName.includes('pi count')) {
      return true; // PI Counts accessible to all
    }
    if (linkUrl.includes('goods-in') || linkName.includes('goods in')) {
      return true; // Goods In accessible to all
    }
    
    // Default: show link only if it's a user's own link or visible_to_all is true
    return link.visible_to_all === true || link.created_by === user?.id;
  };

  // Encon colors
  const enconOrange = '#ee7203';
  const enconBlack = '#1d1d1b';

  // Icon mapping for default apps
  const iconMap = {
    'Transport Manager': Truck,
    'Reports': FileText,
    'Company Car': Car,
    'Settings': Settings,
    'Staffology': ExternalLink,
    'CRM': ExternalLink,
    'Sales Dashboard': DollarSign,
    'Finance': DollarSign,
    'PI Counts': Briefcase,
    'Goods In': Package,
    'Error Log': AlertCircle,
    'Briefcase': Briefcase,
    'Package': Package,
    'AlertCircle': AlertCircle,
  };

  useEffect(() => {
    if (isOpen) {
      loadLinks();
      loadGroups();
    }
  }, [isOpen]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Check if click is not on the dashboard button itself
        const dashboardButton = document.querySelector('[title="Dashboard Menu"]');
        if (dashboardButton && !dashboardButton.contains(event.target)) {
          onClose && onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  const loadGroups = async () => {
    try {
      const response = await api.get(`${API}/dashboard-groups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error loading groups:', error);
      setGroups([]);
    }
  };

  const loadLinks = async () => {
    try {
      // Load custom dashboard links
      const linksResponse = await api.get(`${API}/dashboard-links`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const customLinks = linksResponse.data || [];
      
      // Load hub apps to sync with dropdown
      const hubAppsResponse = await api.get(`${API}/hub-apps`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const hubApps = hubAppsResponse.data || [];
      
      // Create links from hub apps that user has permission to see
      // Only add if they don't already exist in custom links
      const existingUrls = customLinks.map(link => link.url?.toLowerCase());
      const hubLinks = hubApps
        .filter(app => {
          // Check if user has permission for this app
          const hasAccess = app.permission_key 
            ? (user?.role === 'super_admin' || user?.[app.permission_key]) 
            : true;
          
          // Check if link doesn't already exist
          const urlExists = existingUrls.includes(app.route?.toLowerCase());
          
          return hasAccess && !urlExists;
        })
        .map((app, index) => ({
          id: `hub-${app.id}`,
          name: app.name,
          url: app.route,
          icon_type: app.icon_type,
          is_global: true,
          is_external: app.is_external,
          visible_to_all: true,
          permission_key: app.permission_key,
          order: customLinks.length + index,
          isHubApp: true, // Mark as hub app so it can't be deleted
        }));
      
      // Combine custom links and hub links
      setLinks([...customLinks, ...hubLinks]);
    } catch (error) {
      console.error('Error loading dashboard links:', error);
      setLinks([]);
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination || !Array.isArray(links)) return;

    const { source, destination, draggableId } = result;
    
    // If dropped in the same position, do nothing
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Extract group IDs from droppable IDs
    const sourceGroupId = source.droppableId === 'ungrouped' ? null : source.droppableId.replace('group-', '');
    const destGroupId = destination.droppableId === 'ungrouped' ? null : destination.droppableId.replace('group-', '');
    
    // Get source and destination lists
    const sourceLinks = links.filter(l => (sourceGroupId ? l.group_id === sourceGroupId : !l.group_id));
    const destLinks = sourceGroupId === destGroupId 
      ? sourceLinks 
      : links.filter(l => (destGroupId ? l.group_id === destGroupId : !l.group_id));
    
    // Find the dragged link
    const draggedLinkIndex = links.findIndex(l => l.id === draggableId);
    if (draggedLinkIndex === -1) return;
    
    const draggedLink = { ...links[draggedLinkIndex] };
    
    // Remove from source position
    const newLinks = Array.from(links);
    newLinks.splice(draggedLinkIndex, 1);
    
    // Update group_id if moved to different group
    if (sourceGroupId !== destGroupId) {
      draggedLink.group_id = destGroupId;
    }
    
    // Calculate the new position in the full links array
    const destLinksBeforeDrag = newLinks.filter(l => (destGroupId ? l.group_id === destGroupId : !l.group_id));
    const insertIndex = newLinks.findIndex(l => (destGroupId ? l.group_id === destGroupId : !l.group_id));
    const finalInsertIndex = insertIndex === -1 ? newLinks.length : insertIndex + destination.index;
    
    // Insert at destination
    newLinks.splice(finalInsertIndex, 0, draggedLink);
    
    // Update order for all links based on their position
    const updatedLinks = newLinks.map((link, index) => ({
      ...link,
      order: index
    }));

    setLinks(updatedLinks);

    // Save to backend - convert hub app links to persistent links if needed
    try {
      // For hub app links being reordered, we need to create persistent dashboard link entries
      const linksToSave = [];
      
      for (const link of updatedLinks) {
        if (link.isHubApp) {
          // Create a persistent dashboard link for this hub app
          // First check if it already exists in backend, otherwise create it
          try {
            const createResponse = await api.post(`${API}/dashboard-links`, {
              name: link.name,
              url: link.url,
              icon_type: link.icon_type,
              is_global: true,
              is_external: link.is_external || false,
              visible_to_all: true,
              group_id: link.group_id || null,
              order: link.order
            }, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            
            // Use the new ID from backend for future updates
            linksToSave.push({
              id: createResponse.data.id,
              order: link.order,
              group_id: link.group_id || null
            });
          } catch (createError) {
            // Link might already exist, skip creation error
            console.log('Hub app link may already exist:', link.name);
          }
        } else {
          // Regular custom link, just save order
          linksToSave.push({
            id: link.id,
            order: link.order,
            group_id: link.group_id || null
          });
        }
      }
      
      console.log('Saving link order:', linksToSave);
      
      // Save all link orders
      if (linksToSave.length > 0) {
        await api.put(`${API}/dashboard-links/reorder`, 
          { links: linksToSave },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        toast.success('Order saved successfully');
        
        // Reload to get fresh data with new IDs
        await loadLinks();
      }
    } catch (error) {
      console.error('Error reordering links:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save order');
      loadLinks(); // Reload to get correct state from server
    }
  };

  const handleAddLink = () => {
    setEditingLink(null);
    setShowModal(true);
  };

  const handleEditLink = (link) => {
    setEditingLink(link);
    setShowModal(true);
  };

  const handleDeleteLink = async (linkId) => {
    // Check if this is a hub app link
    const link = links.find(l => l.id === linkId);
    
    if (link?.isHubApp) {
      if (!confirm('This will hide this hub app from the dropdown. The app will still be visible on the Hub page. Continue?')) return;
      
      // For hub app links, just remove from local state (they'll come back on reload)
      setLinks(links.filter(l => l.id !== linkId));
      toast.success('Hub app hidden from dropdown');
      return;
    }

    if (!confirm('Are you sure you want to delete this link?')) return;

    try {
      await api.delete(`${API}/dashboard-links/${linkId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Link deleted successfully');
      loadLinks();
    } catch (error) {
      console.error('Error deleting link:', error);
      toast.error('Failed to delete link');
    }
  };

  const handleModalSave = () => {
    loadLinks();
    loadGroups();
    setShowModal(false);
    setEditingLink(null);
  };

  const handleCreateGroup = async (groupName) => {
    try {
      const isGlobal = user?.role === 'super_admin';
      await api.post(`${API}/dashboard-groups`, {
        name: groupName,
        is_global: isGlobal,
        order: Array.isArray(groups) ? groups.length : 0
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Group created successfully');
      loadGroups();
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group');
    }
  };

  // Group links by group_id
  const groupedLinks = () => {
    const grouped = { ungrouped: [] };
    
    // Initialize groups
    if (Array.isArray(groups)) {
      groups.forEach(group => {
        grouped[group.id] = { ...group, links: [] };
      });
    }
    
    // Sort links into groups
    if (Array.isArray(links)) {
      links.forEach(link => {
        if (link.group_id && grouped[link.group_id]) {
          grouped[link.group_id].links.push(link);
        } else {
          grouped.ungrouped.push(link);
        }
      });
    }
    
    return grouped;
  };

  // Render icon with error handling - defined first so it's available
  const renderIcon = (iconType) => {
    // Ensure iconType is valid
    if (!iconType) {
      console.warn('No iconType provided, using default FileText icon');
      return <FileText className="w-8 h-8" style={{ color: enconOrange }} />;
    }
    
    const IconComponent = iconMap[iconType];
    
    if (!IconComponent) {
      console.warn(`Icon "${iconType}" not found in iconMap, using default FileText icon`);
      return <FileText className="w-8 h-8" style={{ color: enconOrange }} />;
    }
    
    return <IconComponent className="w-8 h-8" style={{ color: enconOrange }} />;
  };

  const renderLinkIcon = (link, index, isDragging) => {
    return (
      <div
        className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all relative ${
          isDragging ? 'bg-white/30 shadow-xl' : ''
        } ${
          isEditMode ? 'cursor-move hover:bg-white/20' : 'cursor-pointer hover:bg-white/10'
        }`}
      >
        <div
          className="w-14 h-14 flex items-center justify-center rounded-2xl"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          }}
        >
          {renderIcon(link.iconType || link.icon_type || link.name)}
        </div>
        <span className="text-xs text-white text-center font-medium truncate w-full">
          {link.name || 'Unknown'}
        </span>
        {isEditMode && (user?.role === 'super_admin' || !link.isHubApp) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteLink(link.id);
            }}
            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-all z-10"
          >
            <X className="w-3 h-3 text-white" />
          </button>
        )}
      </div>
    );
  };

  const handleLinkClick = (url) => {
    if (url.startsWith('http')) {
      // External link
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Internal link - use React Router navigation
      navigate(url);
      onClose && onClose(); // Close dropdown after navigation
    }
  };

  // Check if user can edit dashboard
  const canEdit = user?.role === 'super_admin' || user?.can_edit_dashboard_dropdown;

  // Mobile menu - render differently (as a list inside the side panel)
  if (isMobileMenu) {
    return (
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-4 text-gray-500">Loading...</div>
        ) : (
          <>
            {/* Ungrouped links first */}
            {links.filter(link => !link.group_id && canAccessLink(link)).map((link) => {
              const IconComponent = iconMap[link.name] || ExternalLink;
              const hasAccess = canAccessLink(link);
              
              return (
                <button
                  key={link.id}
                  onClick={() => {
                    if (link.is_external && link.url) {
                      window.open(link.url, '_blank');
                    } else if (link.route) {
                      navigate(link.route);
                    }
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                  style={{ opacity: hasAccess ? 1 : 0.5 }}
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: link.color || enconOrange }}
                  >
                    <IconComponent className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium text-gray-700">{link.name}</span>
                </button>
              );
            })}
            
            {/* Grouped links */}
            {groups.map((group) => {
              const groupLinks = links.filter(link => link.group_id === group.id && canAccessLink(link));
              if (groupLinks.length === 0) return null;
              
              return (
                <div key={group.id} className="pt-2">
                  <span className="text-xs text-gray-400 font-medium px-1">{group.name}</span>
                  <div className="mt-1 space-y-1">
                    {groupLinks.map((link) => {
                      const IconComponent = iconMap[link.name] || ExternalLink;
                      return (
                        <button
                          key={link.id}
                          onClick={() => {
                            if (link.is_external && link.url) {
                              window.open(link.url, '_blank');
                            } else if (link.route) {
                              navigate(link.route);
                            }
                            onClose();
                          }}
                          className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: link.color || enconOrange }}
                          >
                            <IconComponent className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-medium text-sm text-gray-700">{link.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  }

  // Desktop/Tablet dropdown
  return (
    <div
      ref={dropdownRef}
      className={`fixed left-0 right-0 transition-all duration-300 ease-in-out overflow-visible ${
        isOpen ? 'opacity-100 animate-slideDown' : 'max-h-0 opacity-0 overflow-hidden pointer-events-none'
      }`}
      style={{
        top: '74px', // Directly under header, on top of sales bar
        zIndex: 50,
      }}
    >
      <div
        className="backdrop-blur-md border-b-2 border-t-4"
        style={{
          backgroundColor: 'rgba(128, 128, 128, 0.85)',
          borderBottomColor: enconOrange,
          borderTopColor: enconOrange,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="px-4 lg:px-8 py-4 max-h-[70vh] overflow-y-auto relative">
          {/* Add Buttons - Only show in edit mode, top area */}
          {canEdit && isEditMode && (
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={handleAddLink}
                className="px-3 py-2 rounded-lg font-semibold text-white transition-all flex items-center gap-2"
                style={{ backgroundColor: enconOrange }}
                title="Add Link"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Link</span>
              </button>
              
              <button
                onClick={() => {
                  const groupName = prompt('Enter group name:');
                  if (groupName) {
                    handleCreateGroup(groupName);
                  }
                }}
                className="px-3 py-2 rounded-lg font-semibold text-white transition-all flex items-center gap-2"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                title="Add Group"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Group</span>
              </button>
            </div>
          )}

          {/* Links Grid - All icons flow horizontally, wrapping to new lines */}
          {loading ? (
            <div className="text-center py-8 text-white">Loading...</div>
          ) : Array.isArray(links) && Array.isArray(groups) ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <div className="flex items-center gap-4 pb-12 flex-wrap">
                {/* Render groups with headers and links */}
                {Array.isArray(groups) && groups.map((group, groupIndex) => {
                  const groupLinks = Array.isArray(links) ? links.filter(link => link.group_id === group.id && (isEditMode || canAccessLink(link))) : [];
                  if (groupLinks.length === 0 && !isEditMode) return null;
                  
                  return (
                    <div key={group.id} className="flex items-center gap-3">
                      {/* Group Header - Inline with icons */}
                      <div className="text-white text-xs font-semibold whitespace-nowrap">
                        {group.name}
                      </div>
                      
                      {/* Group Links - Horizontal flow */}
                      <Droppable droppableId={`group-${group.id}`} direction="horizontal">
                          {(provided) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className="flex items-center gap-2"
                            >
                              {groupLinks.map((link, index) => {
                                // Super admins can drag all links, regular users can only drag their own links
                                const canDrag = isEditMode && (user?.role === 'super_admin' || (!link.isGlobal && !link.isHubApp));
                                return (
                                <Draggable
                                  key={link.id}
                                  draggableId={link.id}
                                  index={index}
                                  isDragDisabled={!canDrag}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      onClick={() => {
                                        if (isEditMode && !snapshot.isDragging) {
                                          handleEditLink(link);
                                        } else if (!isEditMode && !snapshot.isDragging) {
                                          handleLinkClick(link.url);
                                        }
                                      }}
                                      style={{
                                        ...provided.draggableProps.style,
                                      }}
                                    >
                                      {renderLinkIcon(link, index, snapshot.isDragging)}
                                    </div>
                                  )}
                                </Draggable>
                              );
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                        
                        {/* Vertical Orange Divider after each group */}
                        {groupIndex < (Array.isArray(groups) ? groups.length : 0) - 1 && (
                          <div 
                            className="w-0.5 h-16 flex-shrink-0" 
                            style={{ backgroundColor: enconOrange }}
                          />
                        )}
                    </div>
                  );
                })}
                
                {/* Ungrouped Links - Inline with others */}
                {Array.isArray(links) && links.filter(link => !link.group_id && (isEditMode || canAccessLink(link))).length > 0 && (
                  <>
                    {/* Divider before ungrouped if there are groups */}
                    {Array.isArray(groups) && groups.length > 0 && (
                      <div 
                        className="w-0.5 h-16 flex-shrink-0" 
                        style={{ backgroundColor: enconOrange }}
                      />
                    )}
                    <Droppable droppableId="ungrouped" direction="horizontal">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="flex items-center gap-2"
                        >
                          {Array.isArray(links) && links.filter(link => !link.group_id && (isEditMode || canAccessLink(link))).map((link, index) => {
                            // Super admins can drag all links, regular users can only drag their own links
                            const canDrag = isEditMode && (user?.role === 'super_admin' || (!link.isGlobal && !link.isHubApp));
                            return (
                            <Draggable
                              key={link.id}
                              draggableId={link.id}
                              index={index}
                              isDragDisabled={!canDrag}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  onClick={() => {
                                    if (isEditMode && !snapshot.isDragging) {
                                      handleEditLink(link);
                                    } else if (!isEditMode && !snapshot.isDragging) {
                                      handleLinkClick(link.url);
                                    }
                                  }}
                                  style={{
                                    ...provided.draggableProps.style,
                                  }}
                                >
                                  {renderLinkIcon(link, index, snapshot.isDragging)}
                                </div>
                              )}
                            </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </>
                )}
              </div>
            </DragDropContext>
          ) : (
            <div className="text-center py-8 text-white">Initializing...</div>
          )}
          
          {/* Bottom Right Controls - Edit and Dark Mode */}
          <div className="absolute bottom-4 right-4 flex items-center gap-2">
            {/* Dark Mode Toggle */}
            <button
              onClick={() => {
                // Cycle through themes: light -> dark -> system -> light
                if (theme === 'light') {
                  setTheme('dark');
                  toast.success('Dark mode enabled');
                } else if (theme === 'dark') {
                  setTheme('system');
                  toast.success('System theme enabled');
                } else {
                  setTheme('light');
                  toast.success('Light mode enabled');
                }
              }}
              className="p-2 rounded-lg hover:bg-white/10 transition-all"
              title={`Theme: ${theme === 'light' ? 'Light' : theme === 'dark' ? 'Dark' : 'System'}`}
            >
              {theme === 'dark' ? (
                <Moon className="w-5 h-5 text-white" />
              ) : (
                <Sun className="w-5 h-5 text-white" />
              )}
            </button>

            {/* Edit Mode Toggle */}
            {canEdit && (
              <button
                onClick={() => setIsEditMode(!isEditMode)}
                className="px-3 py-2 rounded-lg font-semibold text-white transition-all flex items-center gap-2"
                style={{
                  backgroundColor: isEditMode ? enconOrange : 'rgba(255, 255, 255, 0.2)',
                }}
              >
                {isEditMode ? (
                  <>
                    <X className="w-4 h-4" />
                    <span className="hidden sm:inline">Done</span>
                  </>
                ) : (
                  <>
                    <Edit3 className="w-4 h-4" />
                    <span className="hidden sm:inline">Edit</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        
        {/* Modal for Add/Edit Link */}
        {showModal && (
          <DashboardLinkModal
            isOpen={showModal}
            onClose={() => {
              setShowModal(false);
              setEditingLink(null);
            }}
            onSave={handleModalSave}
            editLink={editingLink}
            groups={groups}
            isSuperAdmin={user?.role === 'super_admin'}
            user={user}
          />
        )}
      </div>
      
      {/* Animation Styles */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.25s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default DashboardDropdown;
